import { ReactNode } from "react";
import {
  ControllerFieldState,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type UseFormReturn,
  UseFormStateReturn,
} from "react-hook-form";

import { Side } from "../ui/customChervonIcon";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input, InputProps } from "@/components/ui/input";
import {
  DEFAULT_MAX_STR_CHARS,
  DEFAULT_MAX_VALUE,
} from "@/constants/constants";
import { cn } from "@/lib/utils";
import { InputKeyLimiter } from "@/utils/helper-functions/form.helper";

type TProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label?: string;
  required?: boolean;
  description?: string;
  htmlFor?: string;
  horizontal?: boolean;
  input?: Omit<InputProps, keyof ControllerRenderProps<FieldValues, string>> & {
    disabled?: boolean;
    allowNegativeNumber?: boolean;
    allowDecimal?: boolean; // For number inputs only
  };
  className?: string;
  charLimit?: number;
  maxValue?: number; // For number inputs only
  render?: <T extends FieldValues>(
    field: ControllerRenderProps<T, Path<T>>,
    fieldState: ControllerFieldState,
    formState: UseFormStateReturn<T>,
  ) => React.ReactNode;
  hideError?: boolean;
  warningMsg?: {
    node: ReactNode;
    position: Side;
  };
};

/**
 *
 * @param {TProps<T>} props - FormInput props
 * @returns {React.ReactNode} - FormInput component
 */
export default function FormInput<T extends FieldValues>(
  props: TProps<T>,
): React.ReactNode {
  const {
    form,
    name,
    label,
    input,
    horizontal,
    htmlFor,
    required,
    description,
    render,
    className,
    // charLimit,
    maxValue,
    hideError = false,
    warningMsg,
  } = props;

  // Destructure conflicting props from inputProps
  const { allowNegativeNumber, allowDecimal, ...inputProps } = input ?? {};

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState, formState }) => (
        <FormItem
          className={cn(
            className,
            horizontal
              ? "flex-row items-center justify-between space-y-0"
              : "3xl:space-y-3 space-y-2",
          )}
        >
          {label && (
            <div className='flex items-center justify-between'>
              {label && (
                <FormLabel htmlFor={htmlFor} className='font-normal w-fit'>
                  {label}{" "}
                  {required && (
                    <span className='text-red-500 font-thin text-xs leading-[8px]'>
                      {" "}
                      *
                    </span>
                  )}
                </FormLabel>
              )}
              {warningMsg && warningMsg.position === "top" && (
                <div className='mr-12'>{warningMsg.node}</div>
              )}
            </div>
          )}
          <FormControl>
            {render ? (
              render(field, fieldState, formState)
            ) : (
              <Input
                {...inputProps}
                {...field}
                pattern={input?.type === "number" ? "d*" : ".*"}
                {...(input?.type === "number" && {
                  onWheel: (e): void => {
                    e.currentTarget.blur();
                  },
                })}
                onKeyDown={(e) =>
                  // InputKeyLimiter(
                  //   e,
                  //   input?.type === "number" ? maxValue : undefined,
                  //   input?.type === "number"
                  //     ? charLimit
                  //     : (charLimit ?? DEFAULT_MAX_STR_CHARS),
                  //   input?.allowNegativeNumber,
                  // )
                  InputKeyLimiter({
                    event: e,
                    maxChars: DEFAULT_MAX_STR_CHARS,
                    ...(input?.type === "number" && {
                      maxValue: maxValue ?? DEFAULT_MAX_VALUE,
                      allowNegative: allowNegativeNumber ?? false,
                      allowDecimal: allowDecimal ?? true,
                    }),
                  })
                }
              />
            )}
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          {!hideError && <FormMessage />}
          {warningMsg && warningMsg.position === "bottom" && warningMsg.node}
        </FormItem>
      )}
    />
  );
}
